/**
 * Teachers magic-link consume page.
 *
 * Landing target of every magic link email. Upgraded per user's directive
 * from "invisible loading spinner" to a crafted ~900ms personalised
 * opening moment:
 *
 *   1. Consume the token (network request)
 *   2. Render "✨ Opening your classroom · {class name} · Ready for assembly tomorrow"
 *      with fade-in + slight upward motion + soft gradient glow
 *   3. Hold for 900ms so the teacher actually SEES the moment
 *   4. Redirect to /classroom/{workspace_id}
 *
 * The 900ms hold is the point: it transforms a login-redirect into a
 * screenshotable "this was made for me" beat. "Ready for assembly
 * tomorrow" anchors the use-case, not just the object — teachers don't
 * care about "classrooms", they care about Monday morning assembly.
 *
 * The class name comes from the consume response (not localStorage) so
 * this works even when the teacher fills Screen 1 on desktop and opens
 * the email on their phone — the backend fetches it from the workspace
 * doc and returns it inline with the session.
 */
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DRAFT_KEY = 'eventdrop_teacher_draft_class_name';
const OPENING_HOLD_MS = 900; // feels personal without stalling

export default function TeachersAuth() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const [error, setError] = useState(null);
  // Consumed data → drives the opening-screen render (class name + redirect)
  const [opening, setOpening] = useState(null); // {workspace_id, workspace_name}
  const consumedRef = useRef(false);

  useEffect(() => {
    if (consumedRef.current) return;
    consumedRef.current = true;
    const params = new URLSearchParams(location.search);
    const token = params.get('token') || '';
    if (!token) {
      setError('This link is missing its token. Request a fresh one from the teacher start page.');
      return;
    }
    (async () => {
      try {
        const { data } = await axios.post(`${API}/auth/teacher/consume`, { token });
        login(data.user, data.session_token);
        try { window.localStorage.removeItem(DRAFT_KEY); } catch { /* ignore */ }
        // Fall back to the localStorage draft if the backend didn't send
        // a name (e.g. returning teacher with a pre-existing classroom
        // that just happens to have no name set).
        let name = data.workspace_name;
        if (!name) {
          try { name = window.localStorage.getItem(DRAFT_KEY) || ''; } catch { /* ignore */ }
        }
        setOpening({
          workspace_id: data.workspace_id,
          workspace_name: name || null,
        });
      } catch (err) {
        setError(err?.response?.data?.detail
          || 'This link is invalid or has expired. Request a fresh one.');
      }
    })();
  }, [location.search, login, navigate]);

  // After opening screen renders, hold for a beat then redirect.
  useEffect(() => {
    if (!opening) return undefined;
    const t = setTimeout(() => {
      if (opening.workspace_id) {
        navigate(`/classroom/${opening.workspace_id}`, { replace: true });
      } else {
        navigate('/classroom', { replace: true });
      }
    }, OPENING_HOLD_MS);
    return () => clearTimeout(t);
  }, [opening, navigate]);

  return (
    <div
      data-testid="teachers-auth"
      style={{
        minHeight: '100vh',
        background: '#0B0F1A',
        color: '#FFFFFF',
        fontFamily: 'Inter, "Helvetica Neue", Helvetica, Arial, sans-serif',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '32px 24px',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Soft gradient glow that pulses behind the class name —
          screenshot-worthy moment. Rendered always (even during consume)
          so the page never flashes black. */}
      <div
        aria-hidden
        style={{
          position: 'absolute',
          top: '50%', left: '50%',
          width: 520, height: 520,
          transform: 'translate(-50%, -50%)',
          borderRadius: '50%',
          background:
            'radial-gradient(circle, rgba(184,151,98,0.14) 0%, rgba(79,195,247,0.06) 45%, transparent 70%)',
          filter: 'blur(28px)',
          animation: 'teachersAuthGlow 3.2s ease-in-out infinite',
          pointerEvents: 'none',
        }}
      />

      <div style={{ maxWidth: 420, width: '100%', textAlign: 'center', position: 'relative', zIndex: 1 }}>
        {/* CONSUMING (pre-data) — short shimmer while the network call is in flight */}
        {!opening && !error && (
          <>
            <div
              aria-hidden
              style={{
                width: 56, height: 56, margin: '0 auto 24px',
                borderRadius: 999,
                background: 'linear-gradient(135deg, rgba(184,151,98,0.24) 0%, rgba(79,195,247,0.22) 100%)',
                backgroundSize: '200% 200%',
                animation: 'teachersAuthShimmer 2s ease-in-out infinite',
              }}
            />
            <div
              style={{
                fontSize: 11, letterSpacing: '0.22em', textTransform: 'uppercase',
                color: '#D4AF37',
              }}
            >
              ✨ Signing you in
            </div>
          </>
        )}

        {/* OPENING (post-consume, pre-redirect) — the crafted 900ms moment */}
        {opening && !error && (
          <div data-testid="teachers-auth-opening" style={{ animation: 'teachersAuthIn 420ms ease-out both' }}>
            <div
              style={{
                fontSize: 11, letterSpacing: '0.22em', textTransform: 'uppercase',
                color: '#D4AF37', marginBottom: 18,
              }}
              data-testid="teachers-auth-opening-eyebrow"
            >
              ✨ Opening your classroom
            </div>
            <h1
              data-testid="teachers-auth-classname"
              style={{
                fontFamily: 'Playfair Display, Georgia, "Times New Roman", serif',
                fontSize: 36, fontWeight: 400, letterSpacing: '-0.015em',
                lineHeight: 1.15, margin: 0, marginBottom: 14,
                // Soft glow tint on the headline itself — reads as "this is mine"
                textShadow: '0 0 24px rgba(184,151,98,0.35)',
                animation: 'teachersAuthName 480ms ease-out 140ms both',
              }}
            >
              {opening.workspace_name || 'Your classroom'}
            </h1>
            <p
              data-testid="teachers-auth-anchor"
              style={{
                fontSize: 13, letterSpacing: '0.04em',
                color: 'rgba(255,255,255,0.55)', margin: 0,
                animation: 'teachersAuthLine 480ms ease-out 260ms both',
              }}
            >
              Ready for assembly tomorrow
            </p>
            {/* Subtle hairline shimmer tying the moment together */}
            <div
              aria-hidden
              style={{
                width: 64, height: 2, margin: '28px auto 0',
                borderRadius: 999,
                background: 'linear-gradient(90deg, transparent, rgba(95,211,243,0.7), transparent)',
                backgroundSize: '200% 100%',
                animation: 'teachersAuthLineSweep 1.6s ease-in-out infinite',
              }}
            />
          </div>
        )}

        {/* ERROR fallback */}
        {error && (
          <>
            <h1
              style={{
                fontFamily: 'Playfair Display, Georgia, "Times New Roman", serif',
                fontSize: 28, fontWeight: 400, letterSpacing: '-0.01em',
                lineHeight: 1.25, margin: 0, marginBottom: 16,
              }}
            >
              We couldn't open your classroom
            </h1>
            <p
              data-testid="teachers-auth-error"
              style={{
                fontSize: 14, color: 'rgba(255,255,255,0.6)',
                marginBottom: 28, lineHeight: 1.6,
              }}
            >
              {error}
            </p>
            <button
              onClick={() => navigate('/teachers/start')}
              data-testid="teachers-auth-retry"
              style={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                gap: 6,
                height: 48, padding: '0 26px', borderRadius: 999, border: 'none',
                cursor: 'pointer',
                background: 'linear-gradient(90deg, #B89762 0%, #4FC3F7 100%)',
                color: '#FFFFFF', fontWeight: 600, fontSize: 14, letterSpacing: '0.02em',
              }}
            >
              Request a fresh link →
            </button>
          </>
        )}
      </div>

      {/* Keyframes — kept inline so this page is self-contained (no CSS imports). */}
      <style>{`
        @keyframes teachersAuthShimmer {
          0%,100% { background-position: 0% 0%; }
          50%     { background-position: 100% 100%; }
        }
        @keyframes teachersAuthGlow {
          0%,100% { opacity: 0.55; transform: translate(-50%, -50%) scale(1); }
          50%     { opacity: 0.85; transform: translate(-50%, -50%) scale(1.06); }
        }
        @keyframes teachersAuthIn {
          from { opacity: 0; filter: blur(6px); transform: translateY(8px); }
          to   { opacity: 1; filter: blur(0);   transform: translateY(0);   }
        }
        @keyframes teachersAuthName {
          from { opacity: 0; transform: translateY(10px); }
          to   { opacity: 1; transform: translateY(0);    }
        }
        @keyframes teachersAuthLine {
          from { opacity: 0; transform: translateY(6px); }
          to   { opacity: 1; transform: translateY(0);   }
        }
        @keyframes teachersAuthLineSweep {
          0%   { background-position: -100% 0%; }
          100% { background-position: 200% 0%; }
        }
      `}</style>
    </div>
  );
}
